import { BarChart3, BookOpen, Home, User } from 'lucide-react';
import { NavLink, useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import { cn } from '@/shared/lib/cn';
import { useInDesktopShell } from '@/shared/lib/desktopLayout';
import { ROUTES } from '@/shared/config';

export interface TabItem {
  to: string;
  labelKey: string;
  icon: typeof Home;
}

// Только корневые разделы. «Мои ошибки» сюда не входит намеренно: это
// подраздел профиля, открывается оттуда (см. `ProfilePage`) и имеет кнопку
// «назад», а не собственную вкладку. «Словарь» — снова вкладка, второй
// после главной (решение Дмитрия 2026-09-26; до того был подразделом профиля).
export const TABS: TabItem[] = [
  { to: ROUTES.home, labelKey: 'home', icon: Home },
  { to: ROUTES.vocabulary, labelKey: 'vocabulary', icon: BookOpen },
  { to: ROUTES.statistics, labelKey: 'statistics', icon: BarChart3 },
  { to: ROUTES.profile, labelKey: 'profile', icon: User },
];

/** Сторона квадратной кнопки таба и скользящего индикатора под ней, px. */
const TAB_SIZE = 52;

/**
 * Нижняя навигация: плавающая «таблетка» поверх контента, а не приклеенная
 * к краю панель — `position: fixed` с обёрткой-центровщиком, а не часть
 * флекс-колонки, иначе панель скроллилась бы вместе с контентом страницы
 * (см. #root: overflow-y-auto в app/styles). Только иконки, без подписей —
 * активная вкладка отмечена не цветом текста, а белым кружком на сером
 * фоне таблетки, который плавно едет под новую иконку при переключении
 * таба. Серый/белый, а не тёмная таблетка из исходного референса — здесь
 * светлая тема приложения (Busuu-палитра: белые карточки на голубовато-
 * сером холсте), тёмный плавающий блок выбивался бы из неё.
 */
export function TabBar() {
  const { pathname } = useLocation();
  const { t } = useTranslation('TabBar');
  // На компьютере в браузере разделы — в сайдбаре (`DesktopShell`).
  const inDesktopShell = useInDesktopShell();
  const activeIndex = TABS.findIndex((tab) => tab.to === pathname);

  if (inDesktopShell) return null;
  return (
    <nav
      aria-label={t('navigation')}
      className="fixed inset-x-0 bottom-0 z-10 flex justify-center pb-[calc(var(--safe-bottom)+0.75rem)]"
    >
      <div className="relative flex items-center rounded-full border border-hairline bg-glass-3 p-1 shadow-[0_6px_20px_rgba(37,43,47,0.18)]">
        {activeIndex >= 0 && (
          <div
            aria-hidden="true"
            className="absolute top-1 left-1 rounded-full bg-glass shadow-[var(--shadow-lg)] transition-transform duration-300 ease-[var(--ease-out)]"
            style={{
              width: TAB_SIZE,
              height: TAB_SIZE,
              transform: `translateX(${activeIndex * TAB_SIZE}px)`,
            }}
          />
        )}
        {TABS.map(({ to, labelKey, icon: Icon }) => (
          <NavLink
            key={to}
            to={to}
            aria-label={t(labelKey)}
            className="relative z-10 flex items-center justify-center"
            style={{ width: TAB_SIZE, height: TAB_SIZE }}
          >
            {({ isActive }) => (
              <Icon
                className={cn('size-6 transition-colors', isActive ? 'text-ink' : 'text-ink-muted')}
                strokeWidth={2}
                aria-hidden="true"
              />
            )}
          </NavLink>
        ))}
      </div>
    </nav>
  );
}
